import { diseaseAPI } from "../../apis";
import { healthScoreGroups, routes } from "../constants";
import {
  RedirectRouteResponse,
  ResponseInfo,
  ResponsesApiResult,
  ScoreQuestion,
} from "../interfaces";

interface ScoreNameWithPrefix {
  scoreName: string;
  respondedQuestionIds: string;
}

// New function to filter questions based on gender and score name
export const filterQuestionsByGender = (
  questions: ScoreQuestion[],
  gender: string,
  scoreName: string
): ScoreQuestion[] => {
  // Map of score names to their female-specific question IDs
  const femaleSpecificQuestionsByScore: Record<string, string[]> = {
    "Medical History 1": [
      "Eira_Clinic_26",
      "Eira_Clinic_27",
      "Eira_Clinic_28",
      "Eira_Clinic_29",
    ],
    // Add more score names and their female-specific question IDs here in the future
  };

  // If this score doesn't have any gender-specific questions, return all questions
  if (!femaleSpecificQuestionsByScore[scoreName]) {
    return questions;
  }

  // Only include female-specific questions if gender is explicitly "female"
  if (gender === "female") {
    return questions;
  } else {
    // For any other gender, exclude female-specific questions
    return questions.filter(
      (question) =>
        !femaleSpecificQuestionsByScore[scoreName].includes(
          question.attributes.question_id
        )
    );
  }
};

export const getHealthHistory = async (
  userId: string,
  client: string,
  scoreNamesWithPrefixes: ScoreNameWithPrefix[] = [
    { scoreName: "Medical History 1", respondedQuestionIds: "Eira_Clinic_" },
    { scoreName: "Medical History 2", respondedQuestionIds: "Eira_Clinic_" },
  ],
  userDetails?: any // Added optional parameter to avoid redundant API call
): Promise<{
  result: ScoreQuestion[];
  respondedQuestionIds: string[];
  totalQuestions: number;
} | null> => {
  try {
    console.log("userDetails-1:", userDetails);

    // Get user details to determine gender - use passed userDetails if available
    let userGender = ""; // Empty string as default

    if (userDetails) {
      // Use the passed userDetails to avoid redundant API call
      userGender =
        userDetails && userDetails.gender
          ? userDetails.gender.toLowerCase()
          : "";
    } else {
      // Fallback to API call if userDetails not provided
      try {
        const fetchedUserDetails = await diseaseAPI.getUserDetails(
          userId,
          client,
          "scoreHelper-gethHealthHistory"
        );
        userGender =
          fetchedUserDetails &&
          fetchedUserDetails.attributes &&
          fetchedUserDetails.attributes.gender
            ? fetchedUserDetails.attributes.gender.toLowerCase()
            : "";
      } catch (error) {
        console.error("Error fetching user details:", error);
        // Continue with empty gender
      }
    }

    // Keep track of which questions belong to which score
    const questionsByScoreName = new Map<string, ScoreQuestion[]>();
    let allQuestions: ScoreQuestion[] = [];
    let fetchFailedForAll = true;

    // Fetch questions for each score name and keep track of their source
    for (const scoreItem of scoreNamesWithPrefixes) {
      try {
        const questions = await diseaseAPI.getScoreQuestions(
          userId,
          scoreItem.scoreName
        );

        if (questions.length > 0) {
          fetchFailedForAll = false;
          questionsByScoreName.set(scoreItem.scoreName, questions);
          allQuestions = [...allQuestions, ...questions];
        }
      } catch (error) {
        console.error(`Error fetching ${scoreItem.scoreName}:`, error);
        // Continue execution with current questions
      }
    }

    // If all requests failed, throw an error
    if (fetchFailedForAll) {
      throw new Error("Failed to fetch any questions for the specified scores");
    }

    // Apply gender filtering to each set of score questions separately
    const filteredQuestions: ScoreQuestion[] = [];

    for (const [scoreName, questions] of questionsByScoreName.entries()) {
      const filteredSet = filterQuestionsByGender(
        questions,
        userGender,
        scoreName
      );
      filteredQuestions.push(...filteredSet);
    }

    // Replace allQuestions with the filtered set
    allQuestions = filteredQuestions;

    // Calculate total questions count
    const totalQuestions = allQuestions.length;

    // Step 2: Extract all question IDs into a single array
    const questionIds = allQuestions.map(
      (question) => question.attributes.question_id
    );

    // Create a map to store the latest response for each question
    const latestResponses = new Map<string, ResponseInfo>();

    // Step 3: Get responses for each score name
    for (const scoreItem of scoreNamesWithPrefixes) {
      try {
        await fetchAllResponses(userId, scoreItem.scoreName, latestResponses);
      } catch (error) {
        console.error(
          `Error fetching ${scoreItem.scoreName} responses:`,
          error
        );
        // Continue with whatever responses we have
      }
    }

    // Create a set to store all responded question IDs
    const respondedIds = new Set<string>();

    // Process each score name's prefix to filter responses
    for (const scoreItem of scoreNamesWithPrefixes) {
      const prefix = scoreItem.respondedQuestionIds;
      // Add IDs that match the prefix to respondedIds
      Array.from(latestResponses.keys())
        .filter((id) => id.startsWith(prefix))
        .forEach((id) => respondedIds.add(id));
    }

    // Get all respondedQuestionIds and sort them numerically
    const respondedQuestionIds = Array.from(respondedIds).sort((a, b) => {
      // For each ID, find its prefix from scoreNamesWithPrefixes
      const prefixA =
        scoreNamesWithPrefixes.find((item) =>
          a.startsWith(item.respondedQuestionIds)
        )?.respondedQuestionIds || "";
      const prefixB =
        scoreNamesWithPrefixes.find((item) =>
          b.startsWith(item.respondedQuestionIds)
        )?.respondedQuestionIds || "";

      // Extract the numeric part after the prefix
      const numA = parseInt(a.replace(prefixA, ""), 10);
      const numB = parseInt(b.replace(prefixB, ""), 10);

      // Handle NaN cases (if parsing fails)
      if (isNaN(numA) && isNaN(numB)) return 0;
      if (isNaN(numA)) return 1;
      if (isNaN(numB)) return -1;

      // Sort numerically (not alphabetically)
      return numA - numB;
    });

    // Step 4: Combine the questions with their responses
    let result = allQuestions.map((question) => {
      const questionId = question.attributes.question_id;
      const response = latestResponses.get(questionId);

      // Create a deep copy of the question to avoid modifying the original
      const questionWithResponse = JSON.parse(
        JSON.stringify(question)
      ) as ScoreQuestion;

      // Add response data if available
      if (response) {
        // Add question_responses array to attributes
        (questionWithResponse.attributes as any).question_responses = [
          response,
        ];
      } else {
        // Add "Not Filled" for questions without responses
        const notFilledResponse: ResponseInfo = {
          question_id: questionId,
          response_value: null,
          response_actual_value: "Not Filled",
          createdAt: new Date().toISOString(),
        };
        (questionWithResponse.attributes as any).question_responses = [
          notFilledResponse,
        ];
      }

      return questionWithResponse;
    });

    // Sort the result array by question_id numeric value
    result = result.sort((a, b) => {
      // For each ID, find its prefix from scoreNamesWithPrefixes
      const questionIdA = a.attributes.question_id;
      const questionIdB = b.attributes.question_id;

      const prefixA =
        scoreNamesWithPrefixes.find((item) =>
          questionIdA.startsWith(item.respondedQuestionIds)
        )?.respondedQuestionIds || "";
      const prefixB =
        scoreNamesWithPrefixes.find((item) =>
          questionIdB.startsWith(item.respondedQuestionIds)
        )?.respondedQuestionIds || "";

      // Extract the numeric part after the prefix
      const numA = parseInt(questionIdA.replace(prefixA, ""), 10);
      const numB = parseInt(questionIdB.replace(prefixB, ""), 10);

      // Handle NaN cases (if parsing fails)
      if (isNaN(numA) && isNaN(numB)) return 0;
      if (isNaN(numA)) return 1;
      if (isNaN(numB)) return -1;

      // Sort numerically (not alphabetically)
      return numA - numB;
    });

    return {
      result,
      respondedQuestionIds,
      totalQuestions,
    };
  } catch (err) {
    console.error("Error fetching health history:", err);
    return null; // Return null in case of error
  }
};

/**
 * Fetches all responses for a given score name and adds them to the responses map
 */
export const fetchAllResponses = async (
  userId: string,
  scoreName: string,
  responsesMap: Map<string, ResponseInfo>
): Promise<void> => {
  let page = 1;
  let hasMorePages = true;
  const pageSize = 100;

  try {
    while (hasMorePages) {
      let response: ResponsesApiResult;

      try {
        response = await diseaseAPI.getIndividualScoreQuestionResponse(
          userId,
          scoreName,
          page,
          pageSize
        );
      } catch (error) {
        console.error(`Error fetching page ${page} for ${scoreName}:`, error);
        break; // Exit the loop if we can't fetch this page
      }

      const { data, meta } = response;

      if (!data || !Array.isArray(data) || data.length === 0) {
        break; // No data to process, exit the loop
      }

      // Process each response
      for (const item of data) {
        try {
          // Safely access nested properties with optional chaining
          const questionId =
            item.attributes.scores_questionnaire?.data?.attributes?.question_id;
          const questionValues =
            item.attributes.scores_questionnaire?.data?.attributes
              ?.question_values || [];

          if (!questionId) {
            continue; // Skip this item if no question ID
          }

          // Parse the response value, with robust error handling
          let responseData;
          try {
            const responseStr = item.attributes.response;

            // Basic validation
            if (typeof responseStr !== "string" || !responseStr.trim()) {
              console.warn(
                `Empty or non-string response for question ${questionId}`
              );
              continue;
            }

            // Attempt standard JSON parsing first
            try {
              responseData = JSON.parse(responseStr);
            } catch (parseError) {
              // If standard parsing fails, try recovery methods
              console.warn(
                `JSON parse failed for ${questionId}, attempting recovery`
              );

              // For malformed JSON that might have a value field
              if (
                responseStr.includes('"value"') ||
                responseStr.includes("value")
              ) {
                try {
                  // Try to extract just the value using regex
                  const valueMatch = responseStr.match(
                    /["|']value["|']\s*:\s*["|']?([^,"}\s]+)["|']?/
                  );
                  if (valueMatch && valueMatch[1]) {
                    responseData = {
                      value: valueMatch[1].replace(/['"]/g, ""),
                    };
                  } else {
                    // Last resort - treat the whole string as the value
                    responseData = { value: responseStr };
                  }
                } catch (regexError) {
                  console.error(`Regex extraction failed for ${questionId}`);
                  responseData = { value: null };
                }
              } else {
                // If no value field is found, use the string as is
                responseData = { value: responseStr };
              }
            }
          } catch (finalError) {
            console.error(`All parsing attempts failed for ${questionId}`);
            responseData = { value: null };
          }

          const responseValue = responseData?.value;

          // Skip if responseValue is still undefined after all recovery attempts
          if (responseValue === undefined) {
            continue;
          }

          // Determine the actual response value
          let responseActualValue = null;
          if (responseValue !== -1 && responseValue !== "-1") {
            // Check if the response contains comma-separated values (multiple selections)
            if (
              typeof responseValue === "string" &&
              responseValue.includes(",")
            ) {
              // Split the comma-separated string into an array of values
              const valueIndices = responseValue.split(",").map((val) => {
                const trimmed = val.trim();
                return !isNaN(Number(trimmed)) ? Number(trimmed) : trimmed;
              });

              // Map each index to its corresponding value from questionValues
              responseActualValue = valueIndices
                .filter((index) => {
                  // Handle both numeric indices and direct string values
                  if (typeof index === "number") {
                    return (
                      !isNaN(index) &&
                      index >= 0 &&
                      index < questionValues.length
                    );
                  }
                  return true; // Keep string values as is
                })
                .map((index) => {
                  // If it's a numeric index, get the corresponding value
                  if (typeof index === "number") {
                    return questionValues[index];
                  }
                  // Otherwise return the string value directly
                  return index;
                });

              // If no valid values were found, set to empty array
              if (!responseActualValue || responseActualValue.length === 0) {
                responseActualValue = [];
              }
            }
            // For Single_Select and similar types, the response is an index
            else if (
              typeof responseValue === "number" ||
              !isNaN(Number(responseValue))
            ) {
              const index = Number(responseValue);
              if (index >= 0 && index < questionValues.length) {
                responseActualValue = questionValues[index];
              }
            }
            // For Checkbox_Search and similar types, the response might be the actual value
            else {
              responseActualValue = responseValue;
            }
          }

          const responseInfo: ResponseInfo = {
            question_id: questionId,
            response_value: responseValue,
            response_actual_value: responseActualValue,
            createdAt: item.attributes.createdAt,
          };

          // Check if we already have a response for this question
          const existingResponse = responsesMap.get(questionId);

          // If no existing response or this one is newer, update the map
          if (
            !existingResponse ||
            new Date(item.attributes.createdAt) >
              new Date(existingResponse.createdAt)
          ) {
            responsesMap.set(questionId, responseInfo);
          }
        } catch (itemError) {
          console.error("Error processing item:", itemError);
          // Continue with the next item
        }
      }

      // Check if we need to fetch more pages
      if (!meta?.pagination) {
        break; // No pagination info, exit the loop
      }

      const { page: currentPage, pageCount } = meta.pagination;
      hasMorePages = currentPage < pageCount;
      page++;
    }
  } catch (error) {
    console.error(`Error in fetchAllResponses for ${scoreName}:`, error);
    // We'll let the error propagate to the caller
    throw error;
  }
};

export const getBloodReportUploads = async (
  userId: string,
  client: string,
  analyticType: string
) => {
  try {
    const response = await diseaseAPI.getUseReportUploads(
      userId,
      client,
      analyticType
    );

    if (!response) {
      console.error("No data found in the response:", response);
      return []; // Return empty array instead of null for consistent handling
    }

    // Apply filter and save the result
    const filteredData = response.filter((item: any) => {
      return item.type === analyticType;
    });

    return filteredData;
  } catch (err) {
    console.error("Error in getBloodReportUploads:", err);
    return []; // Return empty array in case of error
  }
};

export const getFilledTestStatuses = async (
  testNames: string[],
  userId: string,
  clientName: string,
  scoreNamesWithPrefixes?: ScoreNameWithPrefix[]
): Promise<
  Record<
    string,
    {
      isCompleted: boolean;
      minimumRequirement: number;
      maxLimit: number;
      textToDisplay: string;
    }
  >
> => {
  try {
    // Initialize results with all tests as false and default values
    const results: Record<
      string,
      {
        isCompleted: boolean;
        minimumRequirement: number;
        maxLimit: number;
        textToDisplay: string;
      }
    > = {};

    testNames.forEach((test) => {
      results[test] = {
        isCompleted: false,
        minimumRequirement: 0,
        maxLimit: 0,
        textToDisplay: "Status: Pending",
      };
    });

    // const needsVikritiAnalysis = testNames.includes(
    //   "Dosha Imbalance Assessment"
    // );
    const needPraritiProfile = testNames.includes("Prakriti Profiling");
    const needHealthScore = testNames.includes("Health Score");
    const needBloodReportUploads = testNames.includes("Blood Reports");
    const needTongueReportUploads = testNames.includes("Tongue Analysis");
    const healthScoreGroupAnswer = testNames.includes("Health History");

    // Fetch all required data in parallel without caching
    const apiData: {
      allScores?: any;
      vikritiAnalysis?: any;
      healthHistory?: any;
      bloodReportUploads?: any;
      tongueReportUploads?: any;
    } = {};

    const promises = [];

    if (needHealthScore || needPraritiProfile) {
      promises.push(
        diseaseAPI
          .getAllScores(userId)
          .then((result) => {
            apiData.allScores = result;
          })
          .catch((err) => {
            console.error("Error fetching all scores:", err);
          })
      );
    }

    if (healthScoreGroupAnswer) {
      promises.push(
        (apiData.healthHistory = await getHealthHistory(
          userId,
          clientName,
          scoreNamesWithPrefixes
        ))
      );
    }
    // if (needsVikritiAnalysis) {
    //   promises.push(
    //     diseaseAPI
    //       .getVikritiRecommendation(`${userId}@madmachines.in`)
    //       .then((result) => {
    //         apiData.vikritiAnalysis = result;
    //       })
    //       .catch((err) => {
    //         console.error("Error fetching vikriti analysis:", err);
    //       })
    //   );
    // }

    if (needBloodReportUploads) {
      apiData.bloodReportUploads = await getBloodReportUploads(
        userId,
        clientName,
        "blood_report"
      );
    }

    if (needTongueReportUploads) {
      apiData.tongueReportUploads = await getBloodReportUploads(
        userId,
        clientName,
        "tongue"
      );
    }

    // Wait for all API calls to complete
    await Promise.all(promises);

    // Process test statuses based on the fetched API responses
    for (const testName of testNames) {
      if (testName === "Health History" && apiData.healthHistory) {
        const { result, respondedQuestionIds, totalQuestions } =
          apiData.healthHistory;

        results[testName] = {
          isCompleted: respondedQuestionIds.length >= totalQuestions, // As per original code comment: "TODO: revert this change later to false"
          minimumRequirement: respondedQuestionIds.length,
          maxLimit: result?.length || 0,
          textToDisplay: `${respondedQuestionIds.length}/${
            result?.length || 0
          } Filled`,
        };
      }
      if (
        testName === "Prakriti Profiling" &&
        apiData.allScores?.recommendations?.Prakriti
      ) {
        const prakritiData = apiData.allScores.recommendations.Prakriti;

        const hasCompleteQuestions =
          prakritiData.question_responses &&
          prakritiData.questions &&
          (prakritiData.question_responses.length ===
            prakritiData.questions.length ||
            prakritiData.question_responses.length === 35);

        const hasRecommendations =
          prakritiData.recommendations &&
          Object.keys(prakritiData.recommendations).length > 0;

        const isCompleted = hasCompleteQuestions && hasRecommendations;

        results[testName] = {
          isCompleted,
          minimumRequirement: prakritiData?.question_responses?.length || 0,
          maxLimit: prakritiData.questions?.length || 0,
          textToDisplay: isCompleted
            ? prakritiData.label || "Completed"
            : `${prakritiData.question_responses?.length || 0}/${
                prakritiData.questions?.length || 0
              } Filled`,
        };
      }
      // else if (testName === "Dosha Imbalance Assessment") {
      //   results[testName] = {
      //     isCompleted: !!apiData.vikritiAnalysis,
      //     minimumRequirement: 1,
      //     maxLimit: 1,
      //     textToDisplay: "Coming Soon",
      //     // apiData.vikritiAnalysis
      //     //   ? apiData.vikritiAnalysis.vikruti_type || "Status: Completed"
      //     //   : "Status: Pending",
      //   };
      // }
      if (testName === "Dosha Imbalance Assessment") {
        results[testName] = {
          isCompleted: false,
          minimumRequirement: 1,
          maxLimit: 1,
          textToDisplay: "Coming Soon",
          // apiData.vikritiAnalysis
          //   ? apiData.vikritiAnalysis.vikruti_type || "Status: Completed"
          //   : "Status: Pending",
        };
      }
      if (testName === "Health Score" && apiData.allScores?.recommendations) {
        let completedScores = 0;

        // Iterate over all healthScoreGroups
        healthScoreGroups[0].forEach((key) => {
          // Check if this key has recommendations
          if (
            apiData.allScores.recommendations[key.name] &&
            apiData.allScores.recommendations[key.name].recommendations &&
            Object.keys(
              apiData.allScores.recommendations[key.name].recommendations
            ).length > 0
          ) {
            completedScores++;
          }
        });

        const totalScores = healthScoreGroups[0].length;
        const isCompleted = completedScores === totalScores;

        results[testName] = {
          isCompleted,
          minimumRequirement: completedScores,
          maxLimit: totalScores,
          textToDisplay: `${completedScores}/${totalScores}`,
        };
      }
      if (testName === "Blood Reports") {
        // bloodReportUploads will now be a filtered array with just blood_report type
        const uploadCount = apiData.bloodReportUploads?.length || 0;

        results[testName] = {
          isCompleted: uploadCount >= 1,
          minimumRequirement: 1,
          maxLimit: 3,
          textToDisplay:
            uploadCount < 1 ? "Status: Pending" : "Status: Completed",
        };
      } else if (testName === "Tongue Analysis") {
        // tongueReportUploads will now be a filtered array with just tongue_report type
        const uploadCount = apiData.tongueReportUploads?.length || 0;

        results[testName] = {
          isCompleted: uploadCount >= 1,
          minimumRequirement: 1,
          maxLimit: 3,
          textToDisplay:
            uploadCount < 1 ? "Status: Pending" : "Status: Completed",
        };
      }
    }

    return results;
  } catch (err) {
    console.error("Error in getFilledTestStatuses:", err);
    // Return all tests with default values in case of error
    return testNames.reduce(
      (acc, test) => {
        acc[test] = {
          isCompleted: false,
          minimumRequirement: 0,
          maxLimit: 0,
          textToDisplay: "Error: Failed to load",
        };
        return acc;
      },
      {} as Record<
        string,
        {
          isCompleted: boolean;
          minimumRequirement: number;
          maxLimit: number;
          textToDisplay: string;
        }
      >
    );
  }
};

export const redirectRouteCheck = async (
  clinicUserID: string | null,
  currentUser: string | null,
  shouldReturnArray?: string[] // Array of strings like ['userDetails', 'userSymptoms', 'healthHistory']
): Promise<RedirectRouteResponse> => {
  try {
    // Initialize response object
    const response: RedirectRouteResponse = {
      route: "",
      data: shouldReturnArray && shouldReturnArray.length > 0 ? {} : undefined,
    };

    // 1. Check clinicUserID
    if (!clinicUserID || clinicUserID.length === 0) {
      response.route = routes.AboutEira;
      return response;
    }

    // 2. Check userDetails
    const userDetails = await diseaseAPI.getUserDetails(
      clinicUserID,
      currentUser || "",
      "ScoreHElper-redirectRouteCheck"
    );
    if (
      !userDetails ||
      !userDetails.attributes ||
      !userDetails.attributes.name ||
      !userDetails.attributes.time_of_birth ||
      !userDetails.attributes.current_location ||
      !userDetails.attributes.gender
    ) {
      response.route = routes.BasicDetails;

      if (
        shouldReturnArray &&
        response.data &&
        shouldReturnArray.includes("userDetails")
      ) {
        response.data.userDetails = userDetails;
      }

      return response;
    }

    console.log("userDetails-0:", userDetails);
    // 3. Fetch userSymptoms and healthHistory in parallel
    const [userSymptomsResult, healthHistoryResult] = await Promise.allSettled([
      diseaseAPI.getUserSymptomsforPDF(clinicUserID),
      getHealthHistory(clinicUserID, currentUser || "", undefined, userDetails),
    ]);

    // Process results
    const userSymptoms =
      userSymptomsResult.status === "fulfilled"
        ? userSymptomsResult.value
        : null;
    const healthHistory =
      healthHistoryResult.status === "fulfilled"
        ? healthHistoryResult.value
        : null;

    // Check symptoms and health history status
    const hasSymptoms = !!(
      userSymptoms?.data &&
      userSymptoms.data.length > 0 &&
      userSymptoms.data[0]?.attributes?.symptoms?.data &&
      userSymptoms.data[0].attributes.symptoms.data.length > 0
    );

    const hasHealthHistory = !!(
      healthHistory &&
      healthHistory.respondedQuestionIds.length >= healthHistory.totalQuestions
    );

    // Determine route based on symptoms and health history status
    if (!hasSymptoms && !hasHealthHistory) {
      response.route = routes.Symptom;
    } else if (!hasSymptoms && hasHealthHistory) {
      response.route = routes.Dashboard;
    } else if (hasSymptoms && !hasHealthHistory) {
      response.route = `${routes.AboutTest}/Health History`;
    } else {
      // 4. Everything is fine, return Dashboard
      response.route = routes.Dashboard;
    }

    // Include requested data
    if (shouldReturnArray && response.data) {
      if (shouldReturnArray.includes("userDetails")) {
        response.data.userDetails = userDetails;
      }
      if (shouldReturnArray.includes("userSymptoms")) {
        response.data.userSymptoms = userSymptoms;
      }
      if (shouldReturnArray.includes("healthHistory")) {
        response.data.healthHistory = healthHistory;
      }
    }

    return response;
  } catch (err) {
    console.error("Error in redirectRouteCheck:", err);
    return {
      route: routes.AboutEira, // Default fallback route in case of unexpected errors
    };
  }
};
